import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  get
} from '../utils/api';

const SHEET_TYPES = {
  supplies: {
    label: 'Supplies, Spares & Maintenance',
    title: 'Supplies, Spares & Maintenance Physical Stock Count Sheet',
    systemField: 'current_stock'
  },
  parent_materials: {
    label: 'Parent Materials',
    title:
      'Parent Materials Physical Stock Count Sheet',
    systemField: 'current_stock'
  },
  child_skus: {
    label: 'Child SKUs',
    title:
      'Child SKU Physical Stock Count Sheet',
    systemField: 'current_balance'
  },
  finished_goods: {
    label: 'Finished Goods',
    title:
      'Finished Goods Physical Stock Count Sheet',
    systemField: 'current_stock'
  }
};

const escapeHtml = value =>
  String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

const recordStatus = item =>
  item.is_active === false
    ? 'Inactive record with stock'
    : 'Active';

const quantity = value => {
  const number = Number(value || 0);

  if (!Number.isFinite(number)) {
    return '0';
  }

  return number.toLocaleString(
    'en-NG',
    {
      maximumFractionDigits: 4
    }
  );
};

const dateLabel = () =>
  new Intl.DateTimeFormat(
    'en-GB',
    {
      timeZone: 'Africa/Lagos',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }
  ).format(new Date());

const itemSearchText = (
  item,
  sheetType
) => {
  if (sheetType === 'parent_materials' || sheetType === 'supplies') {
    return [
      item.code || item.material_code,
      item.category,
      item.name,
      item.unit_of_measure,
      item.storage_location
    ].join(' ').toLowerCase();
  }

  if (sheetType === 'child_skus') {
    return [
      item.sku,
      item.description,
      item.parent_material_code,
      item.parent_material_name,
      item.inventory_type,
      item.unit_of_measure,
      item.colour,
      item.size_spec,
      item.storage_location
    ].join(' ').toLowerCase();
  }

  return [
    item.sku,
    item.name,
    item.size,
    item.color
  ].join(' ').toLowerCase();
};

const itemIdentity = (
  item,
  sheetType
) => {
  if (sheetType === 'parent_materials' || sheetType === 'supplies') {
    return {
      code: item.code || item.material_code || '',
      name: item.name || '',
      details: [
        item.category,
        item.unit_of_measure,
        recordStatus(item)
      ].filter(Boolean).join(' | '),
      location:
        item.storage_location || ''
    };
  }

  if (sheetType === 'child_skus') {
    const specifications = [
      item.colour,
      item.size_spec,
      item.unit_of_measure
    ].filter(Boolean).join(' | ');

    return {
      code: item.sku || '',
      name:
        item.description ||
        item.sku ||
        '',
      details: [
        item.parent_material_name,
        specifications,
        recordStatus(item)
      ].filter(Boolean).join(' | '),
      location:
        item.storage_location || ''
    };
  }

  return {
    code: item.sku || '',
    name: item.name || '',
    details: [
      item.size,
      item.color,
      recordStatus(item)
    ].filter(Boolean).join(' | '),
    location: ''
  };
};

const buildRows = ({
  items,
  sheetType,
  showSystemQuantities
}) => {
  const definition =
    SHEET_TYPES[sheetType];

  return items.map(
    (item, index) => {
      const identity =
        itemIdentity(
          item,
          sheetType
        );

      const systemQuantity =
        showSystemQuantities
          ? quantity(
              item[
                definition.systemField
              ]
            )
          : '';

      return `
        <tr>
          <td class="number">
            ${index + 1}
          </td>
          <td>
            ${escapeHtml(identity.code)}
          </td>
          <td>
            <strong>
              ${escapeHtml(identity.name)}
            </strong>
            ${
              identity.details
                ? `<div class="details">${escapeHtml(identity.details)}</div>`
                : ''
            }
          </td>
          <td>
            ${escapeHtml(
              identity.location ||
              'Not assigned'
            )}
          </td>
          <td class="quantity">
            ${escapeHtml(systemQuantity)}
          </td>
          <td class="write-cell"></td>
          <td class="write-cell"></td>
          <td class="remarks"></td>
        </tr>
      `;
    }
  ).join('');
};

export default function PhysicalStockCountPrintModal({
  generatedBy,
  onClose
}) {
  const [sheetType, setSheetType] =
    useState('parent_materials');

  const [data, setData] =
    useState({
      supplies: [],
      parent_materials: [],
      child_skus: [],
      finished_goods: []
    });

  const [includeZeroStock, setIncludeZeroStock] =
    useState(true);

  const [
    showSystemQuantities,
    setShowSystemQuantities
  ] = useState(true);

  const [search, setSearch] =
    useState('');

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');

  useEffect(() => {
    let active = true;

    setLoading(true);
    setError('');

    Promise.all([
      get('/inventory-auditor/physical-count-sheet-data'),
      get('/supplies-inventory')
    ])
      .then(([response, suppliesResponse]) => {
        if (!active) return;

        const supplies = suppliesResponse.data || [];
        const supplyIds = new Set(supplies.map(item => item.id));
        setData({
          supplies,
          parent_materials:
            (response.data?.parent_materials || []).filter(item => !supplyIds.has(item.id)),
          child_skus:
            response.data?.child_skus ||
            [],
          finished_goods:
            response.data?.finished_goods ||
            []
        });
      })
      .catch(requestError => {
        if (!active) return;

        setError(
          requestError.message ||
          'Physical count data could not be loaded'
        );
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const visibleItems = useMemo(() => {
    const definition =
      SHEET_TYPES[sheetType];

    const searchValue =
      search.trim().toLowerCase();

    return (
      data[sheetType] || []
    ).filter(item => {
      const stock = Number(
        item[
          definition.systemField
        ] || 0
      );

      if (
        !includeZeroStock &&
        stock === 0
      ) {
        return false;
      }

      if (
        searchValue &&
        !itemSearchText(
          item,
          sheetType
        ).includes(searchValue)
      ) {
        return false;
      }

      return true;
    });
  }, [
    data,
    sheetType,
    includeZeroStock,
    search
  ]);

  const printSheet = () => {
    const definition =
      SHEET_TYPES[sheetType];

    const printWindow = window.open(
      '',
      '_blank'
    );

    if (!printWindow) {
      setError(
        'The print window was blocked. Allow pop-ups for BloansBooks and try again.'
      );

      return;
    }

    printWindow.opener = null;

    const rows = buildRows({
      items: visibleItems,
      sheetType,
      showSystemQuantities
    });

    const systemHeading =
      showSystemQuantities
        ? 'System Qty'
        : 'System Qty Hidden';

    printWindow.document.open();

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>
            ${escapeHtml(definition.title)}
          </title>

          <style>
            @page {
              size: A4 landscape;
              margin: 10mm;
            }

            * {
              box-sizing: border-box;
            }

            body {
              color: #2f241e;
              font-family:
                Arial,
                Helvetica,
                sans-serif;
              font-size: 10px;
              margin: 0;
            }

            h1 {
              color: #3B2A21;
              font-size: 20px;
              margin: 0 0 4px;
              text-align: center;
            }

            .subtitle {
              color: #556B2F;
              font-size: 11px;
              font-weight: 700;
              margin-bottom: 12px;
              text-align: center;
            }

            .meta {
              display: grid;
              grid-template-columns:
                repeat(4, 1fr);
              gap: 8px;
              margin-bottom: 10px;
            }

            .meta div {
              border: 1px solid #8f8177;
              min-height: 29px;
              padding: 6px;
            }

            table {
              border-collapse: collapse;
              table-layout: fixed;
              width: 100%;
            }

            th,
            td {
              border: 1px solid #5e5148;
              padding: 5px;
              vertical-align: top;
            }

            th {
              background: #F2EFE7;
              color: #3B2A21;
              font-size: 9px;
              text-align: left;
            }

            tr {
              break-inside: avoid;
              page-break-inside: avoid;
            }

            .number {
              text-align: center;
              width: 4%;
            }

            .quantity {
              text-align: right;
            }

            .details {
              color: #5f564f;
              font-size: 8px;
              margin-top: 3px;
            }

            .write-cell {
              height: 29px;
            }

            .remarks {
              width: 17%;
            }

            .signatures {
              display: grid;
              grid-template-columns:
                repeat(3, 1fr);
              gap: 14px;
              margin-top: 18px;
            }

            .signature {
              border-top: 1px solid #3B2A21;
              padding-top: 5px;
            }

            .control-note {
              border: 1px solid #A8893D;
              color: #3B2A21;
              font-size: 9px;
              margin-top: 14px;
              padding: 8px;
            }

            .footer {
              color: #6c625b;
              font-size: 8px;
              margin-top: 8px;
              text-align: center;
            }
          </style>
        </head>

        <body>
          <h1>
            ${escapeHtml(definition.title)}
          </h1>

          <div class="subtitle">
            Bloans Creations Limited
          </div>

          <div class="meta">
            <div>
              <strong>Count date:</strong>
              ${escapeHtml(dateLabel())}
            </div>

            <div>
              <strong>Prepared by:</strong>
              ${escapeHtml(generatedBy || '')}
            </div>

            <div>
              <strong>Count location:</strong>
            </div>

            <div>
              <strong>Total listed items:</strong>
              ${visibleItems.length}
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th style="width:4%">No.</th>
                <th style="width:13%">Code / SKU</th>
                <th style="width:25%">Item Description</th>
                <th style="width:12%">Storage Location</th>
                <th style="width:10%">${systemHeading}</th>
                <th style="width:10%">Physical Qty</th>
                <th style="width:9%">Variance</th>
                <th style="width:17%">Condition / Remarks</th>
              </tr>
            </thead>

            <tbody>
              ${
                rows ||
                `
                  <tr>
                    <td colspan="8">
                      No active items match the selected options.
                    </td>
                  </tr>
                `
              }
            </tbody>
          </table>

          <div class="signatures">
            <div class="signature">
              Counted by / Date
            </div>

            <div class="signature">
              Verified by / Date
            </div>

            <div class="signature">
              Management approval / Date
            </div>
          </div>

          <div class="control-note">
            This worksheet is read-only. Differences identified
            during physical counting must be investigated and
            processed through the approved stock-adjustment or
            Inventory reconciliation workflow. Do not overwrite
            BloansBooks balances directly from this sheet.
          </div>

          <div class="footer">
            Generated from BloansBooks on
            ${escapeHtml(dateLabel())}.
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();

    printWindow.focus();

    window.setTimeout(
      () => {
        printWindow.print();
      },
      350
    );
  };

  const definition =
    SHEET_TYPES[sheetType];

  return (
    <div
      className="modal-overlay"
      style={{
        zIndex: 10000
      }}
    >
      <div
        className="modal"
        style={{
          width: 'min(1100px,96vw)',
          maxHeight: '94vh',
          overflow: 'auto'
        }}
      >
        <div
          className="flex-between"
          style={{
            gap: 12,
            alignItems: 'flex-start'
          }}
        >
          <div>
            <h2 style={{marginTop: 0}}>
              Print Physical Count Sheet
            </h2>

            <p className="text-muted text-sm">
              Select the stock category to count.
              The worksheet is read-only and cannot
              alter Inventory.
            </p>
          </div>

          <button
            className="btn btn-ghost"
            type="button"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fit,minmax(190px,1fr))',
            gap: 12,
            marginBottom: 16
          }}
        >
          <label>
            Stock category
            <select
              className="form-control"
              value={sheetType}
              onChange={event => {
                setSheetType(
                  event.target.value
                );

                setSearch('');
              }}
            >
              {Object.entries(
                SHEET_TYPES
              ).map(
                ([
                  value,
                  option
                ]) => (
                  <option
                    key={value}
                    value={value}
                  >
                    {option.label}
                  </option>
                )
              )}
            </select>
          </label>

          <label>
            Search this sheet
            <input
              className="form-control"
              value={search}
              onChange={event =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Code, SKU, item or location"
            />
          </label>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 18,
            flexWrap: 'wrap',
            marginBottom: 16
          }}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7
            }}
          >
            <input
              type="checkbox"
              checked={includeZeroStock}
              onChange={event =>
                setIncludeZeroStock(
                  event.target.checked
                )
              }
            />

            Include zero-stock items
          </label>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7
            }}
          >
            <input
              type="checkbox"
              checked={showSystemQuantities}
              onChange={event =>
                setShowSystemQuantities(
                  event.target.checked
                )
              }
            />

            Show system quantities
          </label>
        </div>

        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}

        {loading ? (
          <div className="spinner" />
        ) : (
          <>
            <div
              style={{
                background: '#F2EFE7',
                borderRadius: 10,
                padding: 12,
                marginBottom: 14
              }}
            >
              <strong>
                {definition.title}
              </strong>

              <div
                className="text-muted text-sm"
                style={{marginTop: 4}}
              >
                {visibleItems.length} active item
                {visibleItems.length === 1
                  ? ''
                  : 's'}{' '}
                will appear on the printed sheet.
              </div>
            </div>

            <div
              style={{
                overflowX: 'auto',
                marginBottom: 16
              }}
            >
              <table className="table">
                <thead>
                  <tr>
                    <th>Code / SKU</th>
                    <th>Item</th>
                    <th>Details</th>
                    <th>Location</th>

                    {showSystemQuantities && (
                      <th>System Qty</th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {visibleItems
                    .slice(0, 50)
                    .map(item => {
                      const identity =
                        itemIdentity(
                          item,
                          sheetType
                        );

                      return (
                        <tr key={item.id}>
                          <td>
                            {identity.code ||
                              'Not assigned'}
                          </td>

                          <td>
                            {identity.name}
                          </td>

                          <td>
                            {identity.details ||
                              'Not specified'}
                          </td>

                          <td>
                            {identity.location ||
                              'Not assigned'}
                          </td>

                          {showSystemQuantities && (
                            <td>
                              {quantity(
                                item[
                                  definition
                                    .systemField
                                ]
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}

                  {visibleItems.length === 0 && (
                    <tr>
                      <td
                        colSpan={
                          showSystemQuantities
                            ? 5
                            : 4
                        }
                      >
                        No active items match the
                        selected options.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {visibleItems.length > 50 && (
              <p className="text-muted text-sm">
                The preview shows the first 50 items.
                All {visibleItems.length} items will be
                included when printed.
              </p>
            )}

            <div
              style={{
                display: 'flex',
                gap: 10,
                justifyContent: 'flex-end',
                flexWrap: 'wrap'
              }}
            >
              <button
                className="btn btn-outline"
                type="button"
                onClick={onClose}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                type="button"
                disabled={
                  Boolean(error) ||
                  loading ||
                  visibleItems.length === 0
                }
                onClick={printSheet}
              >
                Print Count Sheet
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
